import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Globe, Pencil, Plus, Search, Tag, Trash2 } from 'lucide-react';
import { AppError } from '@/core/errors/AppError';
import { useSession } from '@/core/auth/useSession';
import { useSucursales } from '@/data/hooks/useCatalogos';
import { useDisciplinas } from '@/data/hooks/useEmpleados';
import { useDesactivarTarifa, useTarifasAdmin, } from '@/data/hooks/useTarifasAdmin';
import { formatearFecha, formatearMonto } from '@/core/utils/formatters';
import { Button } from '@/shared/components/Button';
import { Chip } from '@/shared/components/Chip';
import { ConfirmDialog } from '@/shared/components/ConfirmDialog';
import { Input } from '@/shared/components/Input';
import { Select } from '@/shared/components/Select';
import { Table } from '@/shared/components/Table';
import { useToast } from '@/shared/components/Toast';
import { TarifaFormDialog } from './TarifaFormDialog';
const MODO_LABEL = {
    POR_GRUPO: 'Por grupo',
    POR_PERSONA: 'Por persona',
};
// Color por modo de precio — alineado a tokens del sistema.
const MODO_TONO = {
    POR_GRUPO: 'info',
    POR_PERSONA: 'success',
};
const ESTADO_LABEL = {
    activa: 'Activa',
    vencida: 'Vencida',
    inactiva: 'Inactiva',
};
const ESTADO_TONO = {
    activa: 'success',
    vencida: 'warning',
    inactiva: 'neutral',
};
function estadoDe(t) {
    if (!t.activo)
        return 'inactiva';
    if (t.vigenteHasta && t.vigenteHasta < hoyIso())
        return 'vencida';
    return 'activa';
}
function hoyIso() {
    return new Date().toISOString().slice(0, 10);
}
export function TarifasPage() {
    const { tieneRol } = useSession();
    const puedeAdmin = tieneRol('ROLE_ADMINISTRAR_TARIFA');
    const toast = useToast();
    const { data: sucursales } = useSucursales();
    const { data: disciplinas } = useDisciplinas();
    const [sucursalId, setSucursalId] = useState('');
    // Defaultear primera sucursal una sola vez al cargar.
    useEffect(() => {
        if (!sucursalId && sucursales?.length)
            setSucursalId(sucursales[0].id);
    }, [sucursales, sucursalId]);
    // Filtros UI
    const [busqueda, setBusqueda] = useState('');
    const [filtroDisciplina, setFiltroDisciplina] = useState('');
    const [filtroModo, setFiltroModo] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('todas');
    // Cuando el filtro pide "inactivas", traer solo inactivas; si no, default (activas).
    const opts = filtroEstado === 'inactiva' ? { activo: false } : undefined;
    const { data: tarifas, isLoading } = useTarifasAdmin(sucursalId || null, opts);
    const desactivar = useDesactivarTarifa();
    const [formOpen, setFormOpen] = useState(false);
    const [editable, setEditable] = useState(null);
    const [desactivando, setDesactivando] = useState(null);
    const tarifasFiltradas = useMemo(() => {
        const q = busqueda.trim().toLowerCase();
        return (tarifas ?? []).filter((t) => {
            if (q && !t.nombre.toLowerCase().includes(q))
                return false;
            if (filtroDisciplina && (t.disciplina ?? '') !== filtroDisciplina)
                return false;
            if (filtroModo && (t.modoPrecio ?? '') !== filtroModo)
                return false;
            if (filtroEstado !== 'todas' && estadoDe(t) !== filtroEstado)
                return false;
            return true;
        });
    }, [tarifas, busqueda, filtroDisciplina, filtroModo, filtroEstado]);
    const totales = useMemo(() => {
        const base = tarifas ?? [];
        return {
            total: base.length,
            activas: base.filter((t) => estadoDe(t) === 'activa').length,
            vencidas: base.filter((t) => estadoDe(t) === 'vencida').length,
            inactivas: base.filter((t) => estadoDe(t) === 'inactiva').length,
        };
    }, [tarifas]);
    const columnas = useMemo(() => [
        {
            key: 'nombre',
            header: 'Nombre',
            cell: (t) => (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "h-4 w-4 flex-shrink-0 rounded-sm border border-border-subtle", style: { backgroundColor: t.color ?? 'transparent' }, title: t.color ? `Color: ${t.color}` : 'Sin color asignado' }), _jsxs("div", { className: "flex flex-col", children: [_jsx("span", { className: "font-medium", children: t.nombre }), _jsxs("span", { className: "text-[10px] text-text-tertiary", children: [t.tipoClase, t.disciplina ? ` · ${t.disciplina}` : ''] })] })] })),
        },
        {
            key: 'personas',
            header: 'Personas',
            cell: (t) => `${t.cantPersonasMin ?? 1}–${t.cantPersonasMax ?? 1}`,
            width: '100px',
            align: 'center',
        },
        {
            key: 'duracion',
            header: 'Duración',
            cell: (t) => (t.duracionHoras != null ? `${t.duracionHoras} h` : '—'),
            width: '100px',
            align: 'center',
        },
        {
            key: 'modo',
            header: 'Modo',
            cell: (t) => {
                const modo = t.modoPrecio ?? '';
                return (_jsx(Chip, { tono: MODO_TONO[modo] ?? 'neutral', children: MODO_LABEL[modo] ?? modo }));
            },
            width: '140px',
        },
        {
            key: 'precio',
            header: 'Precio',
            cell: (t) => (_jsx("span", { className: "num font-semibold", children: formatearMonto(Number(t.precio ?? 0)) })),
            align: 'right',
            width: '120px',
        },
        {
            key: 'vigencia',
            header: 'Vigencia',
            cell: (t) => (_jsxs("span", { className: "text-xs", children: [formatearFecha(t.vigenteDesde), t.vigenteHasta ? ` → ${formatearFecha(t.vigenteHasta)}` : ' →'] })),
            width: '180px',
        },
        {
            key: 'web',
            header: 'Web',
            cell: (t) => t.visibleWeb ? (_jsx(Globe, { className: "h-3.5 w-3.5 text-info" })) : (_jsx("span", { className: "text-text-tertiary", children: "\u2014" })),
            width: '60px',
            align: 'center',
        },
        {
            key: 'estado',
            header: 'Estado',
            cell: (t) => {
                const estado = estadoDe(t);
                return _jsx(Chip, { tono: ESTADO_TONO[estado], children: ESTADO_LABEL[estado] });
            },
            width: '100px',
        },
        ...(puedeAdmin
            ? [
                {
                    key: 'acciones',
                    header: '',
                    width: '100px',
                    align: 'right',
                    cell: (t) => (_jsxs("div", { className: "flex items-center justify-end gap-1", children: [_jsx(Button, { variant: "ghost", size: "icon", onClick: (e) => {
                                    e.stopPropagation();
                                    setEditable(t);
                                    setFormOpen(true);
                                }, "aria-label": "Editar", children: _jsx(Pencil, { className: "h-3.5 w-3.5" }) }), t.activo ? (_jsx(Button, { variant: "ghost", size: "icon", onClick: (e) => {
                                    e.stopPropagation();
                                    setDesactivando(t);
                                }, "aria-label": "Desactivar", className: "text-danger", children: _jsx(Trash2, { className: "h-3.5 w-3.5" }) })) : null] })),
                },
            ]
            : []),
    ], [puedeAdmin]);
    const ejecutarDesactivar = (t) => {
        desactivar.mutate(t.id, {
            onSuccess: () => {
                toast.exito('Tarifa desactivada');
                setDesactivando(null);
            },
            onError: (err) => toast.error(err instanceof AppError ? err.mensaje : 'No pudimos desactivarla.'),
        });
    };
    return (_jsxs("div", { className: "flex flex-col gap-4", children: [_jsxs("div", { className: "flex flex-wrap items-start justify-between gap-3", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-xl font-bold text-text-primary", children: "Tarifas" }), _jsx("p", { className: "text-xs text-text-secondary", children: "Precios por clase. Cada tarifa vale dentro de su rango de vigencia." })] }), puedeAdmin && sucursalId ? (_jsxs(Button, { onClick: () => {
                            setEditable(null);
                            setFormOpen(true);
                        }, children: [_jsx(Plus, { className: "h-4 w-4" }), "Nueva tarifa"] })) : null] }), _jsxs("div", { className: "flex flex-wrap items-end gap-2", children: [_jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[11px] uppercase tracking-wide text-text-tertiary", children: "Sucursal" }), _jsxs(Select, { value: sucursalId, onChange: (e) => setSucursalId(e.target.value), className: "w-56", children: [_jsx("option", { value: "", children: "Seleccion\u00E1 sucursal\u2026" }), sucursales?.map((s) => (_jsx("option", { value: s.id, children: s.nombre }, s.id)))] })] }), _jsxs("div", { className: "relative flex flex-col gap-1", children: [_jsx("span", { className: "text-[11px] uppercase tracking-wide text-text-tertiary", children: "Buscar" }), _jsx(Input, { type: "search", value: busqueda, onChange: (e) => setBusqueda(e.target.value), placeholder: "Nombre\u2026", className: "w-56 pl-8" }), _jsx(Search, { className: "pointer-events-none absolute bottom-2.5 left-2 h-4 w-4 text-text-tertiary" })] }), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[11px] uppercase tracking-wide text-text-tertiary", children: "Disciplina" }), _jsxs(Select, { value: filtroDisciplina, onChange: (e) => setFiltroDisciplina(e.target.value), className: "w-44", children: [_jsx("option", { value: "", children: "Todas" }), disciplinas?.map((d) => (_jsx("option", { value: d.clave, children: d.nombre ?? d.clave }, d.clave)))] })] }), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[11px] uppercase tracking-wide text-text-tertiary", children: "Modo" }), _jsxs(Select, { value: filtroModo, onChange: (e) => setFiltroModo(e.target.value), className: "w-40", children: [_jsx("option", { value: "", children: "Todos" }), _jsx("option", { value: "POR_GRUPO", children: "Por grupo" }), _jsx("option", { value: "POR_PERSONA", children: "Por persona" })] })] }), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[11px] uppercase tracking-wide text-text-tertiary", children: "Estado" }), _jsxs(Select, { value: filtroEstado, onChange: (e) => setFiltroEstado(e.target.value), className: "w-36", children: [_jsx("option", { value: "todas", children: "Todas" }), _jsx("option", { value: "activa", children: "Activas" }), _jsx("option", { value: "vencida", children: "Vencidas" }), _jsx("option", { value: "inactiva", children: "Inactivas" })] })] })] }), sucursalId ? (_jsxs("p", { className: "text-xs text-text-tertiary", children: [_jsxs("span", { className: "font-medium text-text-secondary", children: [tarifasFiltradas.length, tarifasFiltradas.length !== totales.total ? ` / ${totales.total}` : '', ' ', totales.total === 1 && tarifasFiltradas.length === 1 ? 'tarifa' : 'tarifas'] }), ' — ', totales.activas, " ", totales.activas === 1 ? 'activa' : 'activas', filtroEstado === 'inactiva' ? '' : ` · ${totales.vencidas} ${totales.vencidas === 1 ? 'vencida' : 'vencidas'}`, filtroEstado === 'inactiva'
                        ? ` · ${totales.inactivas} ${totales.inactivas === 1 ? 'inactiva' : 'inactivas'}`
                        : ''] })) : null, !sucursalId ? (_jsx("p", { className: "text-sm text-text-tertiary", children: "Eleg\u00ED una sucursal para ver sus tarifas." })) : (_jsx(Table, { columns: columnas, items: tarifasFiltradas, rowKey: (t) => t.id, cargando: isLoading, vacioTitulo: (tarifas?.length ?? 0) > 0 ? 'Sin coincidencias' : 'Sin tarifas', vacioDescripcion: (tarifas?.length ?? 0) > 0
                    ? 'Probá ajustar los filtros o la búsqueda.'
                    : 'Creá la primera tarifa para esta sucursal.', vacioAccion: puedeAdmin && (tarifas?.length ?? 0) === 0 ? (_jsxs(Button, { onClick: () => {
                        setEditable(null);
                        setFormOpen(true);
                    }, children: [_jsx(Tag, { className: "h-4 w-4" }), "Crear la primera"] })) : null })), sucursalId ? (_jsx(TarifaFormDialog, { open: formOpen, onOpenChange: setFormOpen, sucursalId: sucursalId, tarifa: editable })) : null, _jsx(ConfirmDialog, { open: Boolean(desactivando), onOpenChange: (open) => !open && setDesactivando(null), titulo: "Desactivar tarifa", descripcion: desactivando
                    ? `"${desactivando.nombre}" se mantiene en reservas y clases existentes, pero no aparecerá al crear reservas nuevas.`
                    : undefined, textoConfirmar: "Desactivar", destructivo: true, cargando: desactivar.isPending, onConfirmar: () => desactivando && ejecutarDesactivar(desactivando) })] }));
}
